import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  type IBulkGroundItem,
} from '../../api/myLeaguesApi';
import { buildGroundTimingOpen } from '../../utils/groundPayload';
import { FiTrash2, FiPlus, FiChevronDown } from 'react-icons/fi';
import './GroundsStep.scss';

const DEFAULT_COUNTRY_CODE = '91';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DAYS_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;
type DayName = (typeof DAYS_OF_WEEK)[number];

export interface GroundRow {
  localId: string;
  groundName: string;
  groundAddress: string;
  email: string;
  /** Full international value from PhoneInput (includes dial code, digits only) */
  contactNumber: string;
  /** Selected country dial code (e.g. "91") */
  countryCode: string;
  groundDescription: string;
  groundFacilities: string;
  fromTime: string;
  toTime: string;
  daysOpen: DayName[];
}

type RowErrors = Partial<Record<Exclude<keyof GroundRow, 'localId'>, string>>;

const getNationalDigits = (row: GroundRow): string => {
  const digits = (row.contactNumber || '').replace(/\D/g, '');
  const code = (row.countryCode || '').replace(/\D/g, '');
  if (code && digits.startsWith(code)) return digits.slice(code.length);
  return digits;
};

const validateRow = (row: GroundRow): RowErrors => {
  const errors: RowErrors = {};

  if (!row.groundName.trim()) {
    errors.groundName = 'Ground name is required';
  } else if (row.groundName.trim().length < 2) {
    errors.groundName = 'Ground name is too short';
  }

  if (row.email.trim() && !EMAIL_RE.test(row.email.trim())) {
    errors.email = 'Enter a valid email address';
  }

  const nationalDigits = getNationalDigits(row);
  if (!nationalDigits) {
    errors.contactNumber = 'Mobile number is required';
  } else if (nationalDigits.length < 7 || nationalDigits.length > 15) {
    errors.contactNumber = 'Enter a valid mobile number';
  }

  if (row.daysOpen.length === 0) {
    errors.daysOpen = 'Select at least one day';
  }

  return errors;
};

interface GroundsStepProps {
  leagueId: string | null;
  initialRows?: GroundRow[];
  onNext: (rows: GroundRow[]) => void;
  onSkip: () => void;
  onBack?: () => void;
}

type ColumnType = 'text' | 'email' | 'phone' | 'time' | 'days';

const COLUMNS: Array<{
  key: Exclude<keyof GroundRow, 'localId' | 'countryCode'>;
  label: string;
  placeholder: string;
  width: number;
  type: ColumnType;
}> = [
  { key: 'groundName', label: 'GROUND NAME*', placeholder: 'Ground...', width: 269, type: 'text' },
  { key: 'groundAddress', label: 'LOCATION', placeholder: 'Location...', width: 148, type: 'text' },
  { key: 'email', label: 'EMAIL', placeholder: 'Email...', width: 180, type: 'email' },
  { key: 'contactNumber', label: 'MOBILE NUMBER*', placeholder: 'Mobile Number', width: 220, type: 'phone' },
  { key: 'groundDescription', label: 'DESCRIPTION', placeholder: 'Description...', width: 215, type: 'text' },
  { key: 'groundFacilities', label: 'FACILITIES', placeholder: 'Facilities...', width: 242, type: 'text' },
  { key: 'daysOpen', label: 'DAYS OPEN*', placeholder: 'Select days', width: 200, type: 'days' },
  { key: 'fromTime', label: 'FROM TIME*', placeholder: 'From...', width: 150, type: 'time' },
  { key: 'toTime', label: 'TO TIME*', placeholder: 'To...', width: 150, type: 'time' },
];

const makeEmptyRow = (): GroundRow => ({
  localId: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  groundName: '',
  groundAddress: '',
  email: '',
  contactNumber: '',
  countryCode: DEFAULT_COUNTRY_CODE,
  groundDescription: '',
  groundFacilities: '',
  fromTime: '',
  toTime: '',
  daysOpen: [],
});

const isRowEmpty = (row: GroundRow): boolean =>
  !row.groundName.trim() &&
  !row.groundAddress.trim() &&
  !row.email.trim() &&
  !getNationalDigits(row) &&
  !row.groundDescription.trim() &&
  !row.groundFacilities.trim() &&
  !row.fromTime.trim() &&
  !row.toTime.trim() &&
  row.daysOpen.length === 0;

const buildDaysOpenString = (days: DayName[]): string => {
  if (days.length === 0) return '';
  if (days.length === DAYS_OF_WEEK.length) return 'Everyday';
  // preserve week order
  return DAYS_OF_WEEK.filter((d) => days.includes(d)).join(',');
};

const rowToBulkItem = (row: GroundRow): IBulkGroundItem => {
  const contactNumber = getNationalDigits(row);
  return {
    groundName: row.groundName.trim() || undefined,
    groundAddress: row.groundAddress.trim() || undefined,
    email: row.email.trim() || undefined,
    contactNumber: contactNumber || undefined,
    groundDescription: row.groundDescription.trim() || undefined,
    groundFacilities: row.groundFacilities.trim() || '',
    daysOpen: buildDaysOpenString(row.daysOpen) || undefined,
    timingOpen: buildGroundTimingOpen({
      fromTime: row.fromTime.trim() || undefined,
      toTime: row.toTime.trim() || undefined,
    }),
    gMapLink: '',
    matchesPlayedTillDate: 0,
  };
};

interface DaysMultiSelectProps {
  value: DayName[];
  onChange: (next: DayName[]) => void;
  placeholder: string;
}

function DaysMultiSelect({ value, onChange, placeholder }: DaysMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 220,
  });

  useLayoutEffect(() => {
    if (!open || !wrapRef.current) return;
    const updatePos = () => {
      const rect = wrapRef.current!.getBoundingClientRect();
      const menuWidth = Math.max(220, rect.width);
      const viewportH = window.innerHeight;
      const spaceBelow = viewportH - rect.bottom;
      const estMenuHeight = 360;
      const top =
        spaceBelow < estMenuHeight && rect.top > estMenuHeight
          ? rect.top - estMenuHeight - 6
          : rect.bottom + 6;
      const left = Math.min(rect.left, window.innerWidth - menuWidth - 12);
      setMenuPos({ top, left, width: menuWidth });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const allSelected = value.length === DAYS_OF_WEEK.length;
  const display = allSelected
    ? 'Everyday'
    : value.length === 0
      ? ''
      : value.length === 1
        ? DAYS_OF_WEEK.filter((d) => value.includes(d))[0]
        : `${value.length} days selected`;

  const toggleDay = (day: DayName) => {
    if (value.includes(day)) onChange(value.filter((d) => d !== day));
    else onChange([...value, day]);
  };

  const toggleAll = () => {
    if (allSelected) onChange([]);
    else onChange([...DAYS_OF_WEEK]);
  };

  return (
    <div className="grounds-days-select" ref={wrapRef}>
      <button
        type="button"
        className={`grounds-days-trigger ${value.length === 0 ? 'is-empty' : ''} ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="grounds-days-trigger-label">{display || placeholder}</span>
        <FiChevronDown size={14} className="grounds-days-chevron" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="grounds-days-menu"
            role="listbox"
            style={{
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
            }}
          >
            <div className="grounds-days-menu-header">
              <span>Days Open</span>
              <button
                type="button"
                className="grounds-days-clear"
                onClick={() => onChange([])}
                disabled={value.length === 0}
              >
                Clear
              </button>
            </div>
            <label className="grounds-days-option grounds-days-option--all">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              <span>Everyday</span>
            </label>
            <div className="grounds-days-divider" />
            <div className="grounds-days-list">
              {DAYS_OF_WEEK.map((day) => (
                <label key={day} className="grounds-days-option">
                  <input
                    type="checkbox"
                    checked={value.includes(day)}
                    onChange={() => toggleDay(day)}
                  />
                  <span>{day}</span>
                </label>
              ))}
            </div>
            <div className="grounds-days-menu-footer">
              <button
                type="button"
                className="grounds-days-done"
                onClick={() => setOpen(false)}
              >
                Done
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

function GroundsStep({ initialRows, onNext, onSkip, onBack }: GroundsStepProps) {
  const [rows, setRows] = useState<GroundRow[]>(() =>
    initialRows && initialRows.length > 0 ? initialRows : [makeEmptyRow()]
  );
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const updateCell = (
    localId: string,
    key: Exclude<keyof GroundRow, 'localId'>,
    value: string
  ) => {
    setRows((prev) =>
      prev.map((r) => (r.localId === localId ? { ...r, [key]: value } : r))
    );
  };

  const updatePhone = (localId: string, value: string, dialCode: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.localId === localId
          ? { ...r, contactNumber: value, countryCode: dialCode || r.countryCode }
          : r
      )
    );
  };

  const updateDays = (localId: string, days: DayName[]) => {
    setRows((prev) =>
      prev.map((r) => (r.localId === localId ? { ...r, daysOpen: days } : r))
    );
  };

  const addRow = () => {
    setRows((prev) => [...prev, makeEmptyRow()]);
  };

  const removeRow = (localId: string) => {
    setRows((prev) => {
      const next = prev.filter((r) => r.localId !== localId);
      return next.length === 0 ? [makeEmptyRow()] : next;
    });
  };

  const handleNext = () => {
    setSubmitAttempted(true);
    const filled = rows.filter((r) => !isRowEmpty(r));
    if (filled.length === 0) {
      onNext(rows);
      return;
    }

    for (let i = 0; i < filled.length; i++) {
      const errors = validateRow(filled[i]);
      const firstErr = Object.values(errors).find((v) => Boolean(v));
      if (firstErr) {
        const rowIndex = rows.indexOf(filled[i]) + 1;
        toast.error(`Row ${rowIndex}: ${firstErr}`);
        return;
      }
    }
    onNext(rows);
  };

  return (
    <div className="create-league-step-form grounds-step">
      <div className="grounds-bulk-card">
        <div className="grounds-bulk-header">
          <p className="grounds-bulk-title">Add Grounds in Bulk</p>
        </div>
        <div className="grounds-bulk-grid-scroll">
          <div className="grounds-bulk-grid">
            <div className="grounds-grid-row grounds-grid-head">
              <div className="grounds-grid-cell grounds-grid-action-cell" aria-hidden />
              <div className="grounds-grid-cell grounds-grid-num-cell">#</div>
              {COLUMNS.map((col) => (
                <div
                  key={col.key}
                  className="grounds-grid-cell grounds-grid-head-cell"
                  style={{ width: col.width }}
                >
                  {col.label}
                </div>
              ))}
            </div>
            {rows.map((row, idx) => {
              const empty = isRowEmpty(row);
              const errors = !empty && submitAttempted ? validateRow(row) : ({} as RowErrors);
              return (
                <div
                  key={row.localId}
                  className={`grounds-grid-row ${empty ? 'is-placeholder' : ''}`}
                >
                  <div className="grounds-grid-cell grounds-grid-action-cell">
                    {!empty && (
                      <button
                        type="button"
                        className="grounds-grid-delete-btn"
                        aria-label={`Remove ground row ${idx + 1}`}
                        onClick={() => removeRow(row.localId)}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    )}
                  </div>
                  <div className="grounds-grid-cell grounds-grid-num-cell">
                    {idx + 1}
                  </div>
                  {COLUMNS.map((col) => {
                    const err = errors[col.key];
                    return (
                      <div
                        key={col.key}
                        className={`grounds-grid-cell grounds-grid-input-cell grounds-grid-input-cell--${col.type} ${err ? 'has-error' : ''}`}
                        style={{ width: col.width }}
                        title={err || undefined}
                      >
                        {col.type === 'days' ? (
                          <DaysMultiSelect
                            value={row.daysOpen}
                            onChange={(next) => updateDays(row.localId, next)}
                            placeholder={col.placeholder}
                          />
                        ) : col.type === 'time' ? (
                          <input
                            type="time"
                            className="grounds-grid-input grounds-grid-input--time"
                            value={row[col.key] as string}
                            onChange={(e) => updateCell(row.localId, col.key, e.target.value)}
                          />
                        ) : col.type === 'email' ? (
                          <input
                            type="email"
                            className="grounds-grid-input"
                            value={row[col.key] as string}
                            placeholder={col.placeholder}
                            onChange={(e) => updateCell(row.localId, col.key, e.target.value)}
                          />
                        ) : col.type === 'phone' ? (
                          <PhoneInput
                            country="in"
                            value={row.contactNumber}
                            countryCodeEditable={false}
                            onChange={(val, country) => {
                              const dial =
                                country && typeof country === 'object' && 'dialCode' in country
                                  ? String((country as { dialCode?: string }).dialCode ?? '')
                                  : '';
                              updatePhone(row.localId, val, dial);
                            }}
                            inputClass="grounds-grid-phone-input"
                            containerClass="grounds-grid-phone-container"
                            buttonClass="grounds-grid-phone-button"
                            dropdownClass="grounds-grid-phone-dropdown"
                            placeholder={col.placeholder}
                            enableSearch
                          />
                        ) : (
                          <input
                            type="text"
                            className="grounds-grid-input"
                            value={row[col.key] as string}
                            placeholder={col.placeholder}
                            onChange={(e) => updateCell(row.localId, col.key, e.target.value)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        <div className="grounds-bulk-footer">
          <button type="button" className="grounds-add-row-btn" onClick={addRow}>
            <FiPlus size={14} />
            Add another row
          </button>
        </div>
      </div>

      <div className="create-league-step-actions">
        {onBack && (
          <button
            type="button"
            className="create-league-btn-outline"
            onClick={onBack}
          >
            PREVIOUS
          </button>
        )}
        <button
          type="button"
          className="create-league-btn-outline"
          onClick={onSkip}
        >
          SKIP
        </button>
        <button
          type="button"
          className="create-league-btn-primary"
          onClick={handleNext}
        >
          NEXT
        </button>
      </div>
    </div>
  );
}

export const buildGroundBulkItems = (rows: GroundRow[]): IBulkGroundItem[] =>
  rows.filter((r) => !isRowEmpty(r)).map(rowToBulkItem);

export default GroundsStep;
